<template>
  <div class="search-area-control" v-if="isVisible">
    <div class="control-header">
      <span class="radius-value">{{ displayRadius }}</span>
      <button class="close-button" @click="closeControl">×</button>
    </div>
    <input
        type="range"
        class="radius-slider"
        :min="minRadius"
        :max="maxRadius"
        :step="step"
        v-model="radius"
    />

    <div class="additional-controls">
      <div class="toggle-option">
        <input
            type="checkbox"
            id="area-visible"
            :checked="areaVisible"
            @change="toggleArea"
        />
        <label for="area-visible">エリア表示</label>
      </div>

      <!-- 複数ポリゴンモードは初期設定がONの場合のみ表示 -->
      <div class="toggle-option" v-if="multiplePolygonsFeatureEnabled">
        <input
            type="checkbox"
            id="multiple-mode"
            v-model="multiplePolygonsModeComputed"
        />
        <label for="multiple-mode">複数地点</label>
      </div>
      <button v-if="multiplePolygonsFeatureEnabled" class="clear-button" @click="clearAllPolygons">全削除</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import {ref, computed, onMounted, watch} from 'vue'
import {mapDrawLayerManager} from '@/services/MapDrawLayerManager.ts'

// 基本設定
const radius = ref(mapDrawLayerManager.area.state.options.radius)
const step = ref(100)
const center = ref<google.maps.LatLng | null>(null)

// UI表示制御
const controlVisible = ref(false)
const lastSearchTime = ref(0)

// 複数ポリゴン機能関連
const multiplePolygonsFeatureEnabled = computed(() => {
  return mapDrawLayerManager.area.state.multiplePolygons !== undefined
})

const multiplePolygonsModeComputed = computed({
  get: () => !!mapDrawLayerManager.area.state.multiplePolygons,
  set: (value) => value ?
    mapDrawLayerManager.area.enableMultiplePolygonsMode() :
    mapDrawLayerManager.area.disableMultiplePolygonsMode()
})

// 検索イベント検知と表示更新
const checkSearchEvents = () => {
  if (mapDrawLayerManager.area.state.centerPoint) {
    const now = Date.now()
    // 検索操作と判断して表示する
    if (now - lastSearchTime.value > 1000) {
      controlVisible.value = true
      lastSearchTime.value = now
    }
  }
}

// ポリゴンの表示状態
const areaVisible = computed(() => {
  return mapDrawLayerManager.area.state.options.showArea === true ||
      (mapDrawLayerManager.area.state.options.showArea === undefined &&
       mapDrawLayerManager.area.state.activeFeatureId !== null)
})

const minRadius = computed(() => mapDrawLayerManager.area.state.options.minRadius)
const maxRadius = computed(() => mapDrawLayerManager.area.state.options.maxRadius)

// コントロール表示条件
const isVisible = computed(() => {
  return mapDrawLayerManager.area.state.enabled &&
         mapDrawLayerManager.getDataLayer() !== null &&
         controlVisible.value
})

// 表示コントロール関数
const closeControl = () => {
  mapDrawLayerManager.area.remove()
  controlVisible.value = false
}

// 半径表示フォーマット
const displayRadius = computed(() => {
  if (Number(radius.value) >= 1000) {
    return `${(Number(radius.value) / 1000).toFixed(1)}km`
  }
  return `${radius.value}m`
})

// 全ポリゴン削除
const clearAllPolygons = () => {
  mapDrawLayerManager.area.remove()
}

// エリア表示切替
const toggleArea = () => {
  const newVisibility = !areaVisible.value
  console.debug("newVisibility", newVisibility)
  mapDrawLayerManager.area.updateOptions({showArea: newVisibility})
}

onMounted(() => {
  // 初期状態を設定


  if (mapDrawLayerManager.area.state.centerPoint) {
    center.value = mapDrawLayerManager.area.state.centerPoint
  }

  // 中心点の変更を監視して検索イベントを検知
  watch(() => mapDrawLayerManager.area.state.centerPoint, () => {
    checkSearchEvents()
  })

  // 半径の監視
  watch(() => mapDrawLayerManager.area.state.options.radius, (newRadius) => {
    if (newRadius !== Number(radius.value)) {
      radius.value = newRadius
    }
  })

  watch(radius, (newValue) => {
    const numValue = Number(newValue)
    // centerの有無に関わらず更新する
    mapDrawLayerManager.area.updateOptions({radius: numValue})

    // アクティブなフィーチャーがあれば更新
    const featureId = mapDrawLayerManager.area.state.activeFeatureId
    if (featureId) {
      mapDrawLayerManager.area.updateRadius(numValue, featureId)
    }
  })
})
</script>

<style lang="scss" scoped>
.search-area-control {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: white;
  padding: 8px 12px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 1;
  width: 150px; /* 幅を広げる */
  border: solid 1px #ddd;

  .control-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;

    .control-toggle {
      background: none;
      border: none;
      color: #4285F4;
      cursor: pointer;
      font-size: 12px;
      padding: 0;

      &:hover {
        color: #62A5FF;
      }
    }
  }

  .radius-slider {
    width: 100%;
    height: 6px;
    border-radius: 3px;
    background: #e0e0e0;
    outline: none;
    margin-bottom: 12px;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #4285F4;
      cursor: pointer;
    }

    &::-moz-range-thumb {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #4285F4;
      cursor: pointer;
      border: none;
    }
  }

  .additional-controls {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 10px;
    border-top: 1px solid #eee;
    padding-top: 10px;
    font-size: 0.75rem;

    .toggle-option {
      display: flex;
      align-items: center;
      gap: 6px;

      input[type="checkbox"] {
        margin: 0;
      }
    }

    .multiple-toggle {
      display: flex;
      align-items: center;


      input[type="checkbox"] {
        margin-right: 4px;
      }
    }

    .clear-button {
      background: #f5f5f5;
      border: 1px solid #ddd;
      border-radius: 4px;
      padding: 4px 8px;
      color: #555;
      cursor: pointer;

      &:hover {
        background: #eee;
        color: #333;
      }
    }
  }

  .control-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .close-button {
    background: none;
    border: none;
    font-size: 1rem;
    cursor: pointer;
    color: #999;
    padding: 0 4px;

    &:hover {
      color: #333;
    }
  }
}
</style>